<script>
import { formButton, formInput } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, provide, onMounted, computed, watch } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { getPlatformFromUrl, platformWhiteLabel } from '@/composables/utils/useWhitelabelService.js'
import { checkCode } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterPJStep3',
  components: {
    RegisterLayout,
    formInput,
    formButton,
    StepProgressBar
  },

  setup() {
    useMeta({ title: 'Cadastro PJ - Etapa 3' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()

    onMounted(() => {
      validateStepAccess(onboardingStore, 'PJ', [1, 2], router)
    })

    const step3Data = onboardingStore.getStepData(3) || {}

    const subscriptionCodeValue = ref(step3Data.subscriptionCode || '')
    const subscriptionCodeType = ref('')

    const hasCodePartner = computed(() => {
      return platformWhiteLabel(getPlatformFromUrl(), 'codePartner') != null
    })

    const loading = ref(false)

    const demandValue = ref(step3Data.demand || '')
    const customDemandValue = ref(step3Data.customDemand || '')

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    watch(demandValue, (val) => {
      setFieldValue('demand', val)
    })

    const { handleSubmit, errors, setFieldValue } = useForm({
      validationSchema: yup.object({
        subscriptionCode: yup.string(),
        demand: yup.string().required('Selecione um motivo para operações de câmbio'),
        customDemand: yup.string().test(function (value) {
          if (demandValue.value === 'Outros') {
            return !!value
          }
          return true
        })
      }),
      initialValues: {
        demand: demandValue.value
      }
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true

      const isInvalidCode = await validateCode()
      if (isInvalidCode) return

      onboardingStore.setStepData(3, {
        subscriptionCode: subscriptionCodeValue.value,
        subscriptionCodeType: subscriptionCodeType.value,
        demand: demandValue.value,
        customDemand: customDemandValue.value
      })

      router.push('/register/pj/step-4')
    })

    const statusResponse = ref(null)
    const invalidCodeMessage = ref('')

    const validateCode = async () => {
      if (!subscriptionCodeValue.value?.trim()) return false

      try {
        const bodySubscriptionCode = {
          client_type: onboardingStore.typeClient,
          refer_code: encodeURIComponent(subscriptionCodeValue.value.trim()),
          institution: platformWhiteLabel(getPlatformFromUrl(), 'institution').toUpperCase()
        }

        const { data } = await checkCode(bodySubscriptionCode)
        statusResponse.value = data?.payload?.status

        const statusMessages = {
          EXPIRED: 'Código expirado!',
          DOES_NOT_EXIST: 'Código não encontrado!',
          INVALID_TYPE:
            onboardingStore.typeClient === 'pj'
              ? 'Código válido apenas para cadastros PF.'
              : 'Código válido apenas para cadastros PJ.',
          INVALID_PARTNER: 'Código válido, contacte o suporte.',
          INVALID_PROMOCODE: 'Código válido, contacte o suporte.'
        }

        if (statusMessages[statusResponse.value]) {
          invalidCodeMessage.value = statusMessages[statusResponse.value]
          toast(invalidCodeMessage.value, { autoClose: 3000, theme: 'auto', type: 'error' })
          return true
        }

        subscriptionCodeType.value = data?.payload?.discountType
        return false
      } catch (error) {
        handleGlobalError(error, 'Erro ao validar o código:')
        toast('Erro ao validar o código. Tente novamente mais tarde.', {
          autoClose: 3000,
          theme: 'auto',
          type: 'error'
        })
        return true
      } finally {
        loading.value = false
      }
    }

    const demandOptions = [
      'Recebimento ou pagamento por serviços',
      'Importação ou exportação',
      'Produção de conteúdo',
      'Aporte financeiro',
      'Frete',
      'Outros'
    ]

    return {
      hasCodePartner,
      subscriptionCodeValue,
      loading,
      onSubmitAction,
      statusResponse,

      demandValue,
      demandOptions,
      customDemandValue,
      alreadySubmittedOnce,

      errors
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar
          class="mt-5 mb-5"
          :steps="['Empresa', 'Responsável', 'Demanda', 'Senha']"
          :current="3"
        />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-input
            v-if="!hasCodePartner"
            class="col-12 form-input mb-3"
            label="Possui código de indicação/promocional? (opcional)"
            v-model="subscriptionCodeValue"
            placeholder="#EXEMPLO"
            id="subscriptionCode"
            name="subscriptionCode"
            type="text"
            :isInvalid="
              [
                'DOES_NOT_EXIST',
                'EXPIRED',
                'INVALID_TYPE',
                'INVALID_PARTNER',
                'INVALID_PROMOCODE'
              ].includes(statusResponse)
            "
          />

          <div class="col-12 mt-3">
            <label class="mb-2 d-block fw-bold">
              Qual a sua demanda para operações de câmbio?
            </label>

            <div class="radio-card-group stacked">
              <label
                v-for="(option, index) in demandOptions"
                :key="index"
                class="radio-card-label"
                :class="{ selected: demandValue === option }"
              >
                <input
                  type="radio"
                  :value="option"
                  v-model="demandValue"
                  id="demand"
                  name="demand"
                />
                <span class="custom-radio"></span>
                <span class="label-text">{{ option }}</span>
              </label>
            </div>

            <div v-if="alreadySubmittedOnce && errors.demand" class="text-danger text-center error">
              {{ errors.demand }}
            </div>

            <form-input
              v-if="demandValue === 'Outros'"
              class="col-12 form-input mt-2"
              label="Descreva qual o motivo para suas operações de câmbio"
              placeholder="Ex: Pagamento de dívida no exterior"
              v-model="customDemandValue"
              id="customDemand"
              name="customDemand"
              type="text"
            />
          </div>

          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
